import { Volume2, VolumeX, Play } from "lucide-react";
import { useState, useRef, useEffect } from "react";

/**
 * Reproductor vertical (9:16) de testimonios — mismas características que el
 * video del Hero de la Home: autoplay muted, botón de silencio/sonido,
 * marco redondeado con ring + shadow, y fallback si aún no hay video cargado.
 * Robustez: si el player nunca confirma reproducción (bot-detection, bloqueo
 * de red, ad-blocker) muestra la miniatura de YouTube con link al video en
 * vez del error nativo o un cuadro en blanco.
 */
export default function TestimonialVideoPlayer({ videoId, posterImage, name }) {
  const [isMuted, setIsMuted] = useState(true);
  const [videoEnded, setVideoEnded] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const iframeRef = useRef(null);
  const playerRespondedRef = useRef(false);
  const testId = name.toLowerCase().replace(/\s+/g, "-");
  const thumbnail = videoId ? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg` : null;
  const watchUrl = videoId ? `https://www.youtube.com/watch?v=${videoId}` : null;

  const toggleMute = () => {
    if (!iframeRef.current) return;
    const func = isMuted ? "unMute" : "mute";
    iframeRef.current.contentWindow.postMessage(
      JSON.stringify({ event: "command", func, args: "" }),
      "*"
    );
    setIsMuted(!isMuted);
  };

  useEffect(() => {
    if (!videoId) return;
    const onMessage = (e) => {
      if (!e.data || typeof e.data !== "string") return;
      // Con 2+ players en la misma página, ignorar mensajes de otros iframes
      if (!iframeRef.current || e.source !== iframeRef.current.contentWindow) return;
      try {
        const msg = JSON.parse(e.data);
        if (msg.event === "onReady") {
          playerRespondedRef.current = true;
        }
        if (msg.event === "onError") {
          setVideoFailed(true);
        }
        if (msg.event === "onStateChange") {
          playerRespondedRef.current = true;
          if (msg.info === 0) setVideoEnded(true);
        }
      } catch (_) {}
    };
    window.addEventListener("message", onMessage);

    // Si el player nunca respondió (embed bloqueado), caer al fallback
    const failSafeTimer = setTimeout(() => {
      if (!playerRespondedRef.current) setVideoFailed(true);
    }, 6000);

    return () => {
      window.removeEventListener("message", onMessage);
      clearTimeout(failSafeTimer);
    };
  }, [videoId]);

  if (!videoId) {
    return (
      <div
        data-testid={`testimonial-video-placeholder-${testId}`}
        className="relative w-full max-w-[280px] mx-auto lg:mx-0 aspect-[9/16] rounded-2xl overflow-hidden ring-1 ring-slate-200 bg-[#0A1128] shadow-[0_25px_60px_-20px_rgba(10,17,40,0.35)] flex flex-col items-center justify-center gap-3"
      >
        <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
          <Play className="w-5 h-5 text-white/60" fill="currentColor" />
        </div>
        <p className="text-[11px] font-bold tracking-[0.16em] uppercase text-white/50">
          Video próximamente
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid={`testimonial-video-${testId}`}
      className="relative w-full max-w-[280px] mx-auto lg:mx-0 aspect-[9/16] rounded-2xl overflow-hidden ring-1 ring-slate-200 shadow-[0_25px_60px_-20px_rgba(10,17,40,0.35)]"
    >
      <img
        src={posterImage || thumbnail}
        alt={name}
        className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
        style={{ opacity: videoEnded || videoFailed ? 1 : 0 }}
      />
      <iframe
        ref={iframeRef}
        className="absolute inset-0 w-full h-full transition-opacity duration-700"
        style={{
          opacity: videoEnded || videoFailed ? 0 : 1,
          pointerEvents: videoEnded || videoFailed ? "none" : "auto",
        }}
        src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&controls=0&rel=0&playsinline=1&enablejsapi=1`}
        title={`Testimonio de ${name}`}
        frameBorder="0"
        referrerPolicy="strict-origin-when-cross-origin"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
      {videoFailed && (
        <a
          href={watchUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`testimonial-video-fallback-${testId}`}
          className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-[#0A1128]/70 backdrop-blur-sm"
        >
          <div className="w-12 h-12 rounded-full bg-[#F26522] flex items-center justify-center shadow-lg shadow-[#F26522]/40">
            <Play className="w-5 h-5 text-white" fill="currentColor" />
          </div>
          <span className="text-[11px] font-bold tracking-[0.16em] uppercase text-white">
            Ver video en YouTube
          </span>
        </a>
      )}
      {!videoEnded && !videoFailed && (
        <button
          onClick={toggleMute}
          data-testid={`testimonial-video-mute-toggle-${testId}`}
          aria-label={isMuted ? "Activar sonido" : "Silenciar video"}
          className="absolute bottom-4 left-4 z-10 flex items-center gap-1.5 bg-black/50 hover:bg-black/70 text-white text-xs font-semibold px-2.5 py-1.5 rounded-full backdrop-blur-sm transition-all"
        >
          {isMuted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
          {isMuted ? "Sonido" : "Silenciar"}
        </button>
      )}
    </div>
  );
}
